import {Backdrop, CircularProgress, Typography, makeStyles} from '@material-ui/core';
import {isLoaded} from 'Core/models/Async';
import {SelectionItem, SelectionList, SelectionListProps} from 'Quoting/components/shared/SelectionList';
import {initCreateQuote, setSelectedProduct} from 'Quoting/ducks/createQuote';
import {CreateQuoteRequestPayload} from 'Quoting/models/DTOs/QuoteForm';
import {A360ProductCd, A360ProductModel, Product} from 'Quoting/models/Quoting';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import React, {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getA360ProductList, productTypeLabel} from './products';
import { getOpportunityURI } from 'Quoting/utils/utilities';

const useProductLabelStyles = makeStyles(() => ({
  productLabel: {
    display: 'flex',
    flexDirection: 'column',
    '& .lbl-main': {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: 50,
      backgroundColor: '#EDF6FC',
      color: '#0033A0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1rem',
      fontWeight: 600,
    },
    '& .lbl-secondary': {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: '1.05rem',
      textAlign: 'left',
      color: '#333333',
      boxShadow: 'none',
    },
  },
}));

const useCreateQuoteLoadingStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

const ProductLabel = ({title}: {title: React.ReactNode}) => {
  const classes = useProductLabelStyles();
  return (
    <div className={classes.productLabel}>
      <Typography variant="body2" className="lbl-secondary">
        {title}
      </Typography>
    </div>
  );
};

const PrimaryTitle = () => (
  <Typography variant="body1" style={{color: '#2F3847'}}>
    Group
  </Typography>
);

type ProductSelectionProps = Omit<SelectionListProps<Product>, 'onContinue' | 'header' | 'items' | 'identifier'>;

export const productOptions = [
  {
    name: 'Accident',
    code: A360ProductCd.ACC,
    _model: A360ProductModel.ACCMaster,
  },
  {
    name: 'Critical Illness',
    code: A360ProductCd.CI,
    _model: A360ProductModel.CIMaster,
  },
  {
    name: 'Hospital Indemnity',
    code: A360ProductCd.HI,
    _model: A360ProductModel.HIMaster,
  },
];

const gpluProduct = {
  name: 'Permanent Life',
  code: A360ProductCd.GPLU,
  _model: A360ProductModel.GPLUMaster,
};

export const getProductByModel = (productModel: A360ProductModel) => {
  switch (productModel) {
    case A360ProductModel.ACCMaster:
      return productOptions[0];
    case A360ProductModel.CIMaster:
      return productOptions[1];
    case A360ProductModel.HIMaster:
      return productOptions[2];
    case A360ProductModel.GPLUMaster:
      return gpluProduct;
    default:
      return null;
  }
};

export const ProductSelection = (props: ProductSelectionProps) => {
  const dispatch = useDispatch();

  const classes = useCreateQuoteLoadingStyles();

  const {opportunitySummary, selectedOpportunityId} = useSelector((state: ReducerState) => state.opportunity);

  const {quote} = useSelector((state: ReducerState) => state.quoteForm);

  const {BR_QuotingTool_GPLU} = useSelector((state: ReducerState) => state.featureFlag);

  const products = getA360ProductList(Boolean(BR_QuotingTool_GPLU));

  const productList = useMemo(
    () =>
      Array.isArray(products)
        ? products?.map<Product & SelectionItem>(product => ({
            ...product,
            key: toKebabCase(product.name),
            label: <ProductLabel title={product.name} />,
          }))
        : [],
    [products, BR_QuotingTool_GPLU]
  );

  const onConfirmProductSelection = useCallback(
    (selectedProductModel: A360ProductModel) => {
      const selectedProduct = products.find(p => p._model === selectedProductModel);
      dispatch(setSelectedProduct(selectedProduct));

      const createQuotePayload: CreateQuoteRequestPayload = {
        country: 'US',
        currencyCd: 'USD',
        customer: {
          _uri: isLoaded(opportunitySummary) ? opportunitySummary.Data?.customer?._uri : '',
        },
        opportunity: {
          _uri: isLoaded(opportunitySummary) ? getOpportunityURI(opportunitySummary.Data?._key?.rootId) : '',
        },
      };

      dispatch(initCreateQuote(selectedProductModel, createQuotePayload, selectedOpportunityId));
    },
    [opportunitySummary.key, selectedOpportunityId, BR_QuotingTool_GPLU]
  );

  return (
    <>
      <Backdrop className={classes.backdrop} open={quote.key === 'Loading'}>
        <CircularProgress color="primary" />
      </Backdrop>
      <SelectionList<Product>
        items={productList}
        identifier="_model"
        groupBy={{
          key: '_type',
          labels: productTypeLabel,
        }}
        header={{
          primaryTitle: <PrimaryTitle />,
        }}
        onCancel={props.onCancel}
        onContinue={(_model: A360ProductModel) => onConfirmProductSelection(_model)}
      />
    </>
  );
};
